/**
 * スケジュール可視性設定フォームコンポーネント
 * FL-114: 陸送員スケジュール公開範囲設定機能
 */

import React from 'react';
import { DriverScheduleVisibility } from '../../types';
import { Button } from '../ui';
import logger from '../../utils/logger';
import { EyeIcon, EyeSlashIcon, UsersIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

interface ScheduleVisibilitySettingsFormProps {
  visibility: DriverScheduleVisibility;
  onChange: (visibility: DriverScheduleVisibility) => void;
  onSave: () => Promise<void>;
  isLoading?: boolean;
}

const ScheduleVisibilitySettingsForm: React.FC<ScheduleVisibilitySettingsFormProps> = ({
  visibility,
  onChange,
  onSave,
  isLoading = false,
}) => {
  const handleVisibilityChange = (newVisibility: DriverScheduleVisibility) => {
    logger.info('🔄 スケジュール可視性設定変更', {
      from: visibility,
      to: newVisibility,
    });
    onChange(newVisibility);
  };

  const handleSave = async () => {
    logger.info('🔄 スケジュール可視性設定保存開始', { visibility });
    await onSave();
  };

  return (
    <div className='space-y-6'>
      <div>
        <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-2'>
          陸送員スケジュール公開範囲設定
        </h3>
        <p className='text-sm text-gray-600 dark:text-gray-300'>
          陸送員が他の陸送員のスケジュールを閲覧できる範囲を設定します。
          自分自身のスケジュールは常に閲覧可能です。
        </p>
      </div>

      <div className='space-y-4'>
        {/* 管理者のみ設定 */}
        <div
          className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
            visibility === DriverScheduleVisibility.ADMIN_ONLY
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
          }`}
          onClick={() => handleVisibilityChange(DriverScheduleVisibility.ADMIN_ONLY)}
        >
          <div className='flex items-start space-x-3'>
            <div
              className={`mt-1 ${
                visibility === DriverScheduleVisibility.ADMIN_ONLY
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              <ShieldCheckIcon className='h-5 w-5' />
            </div>
            <div className='flex-1'>
              <div className='flex items-center space-x-2'>
                <input
                  type='radio'
                  checked={visibility === DriverScheduleVisibility.ADMIN_ONLY}
                  onChange={() => handleVisibilityChange(DriverScheduleVisibility.ADMIN_ONLY)}
                  className='h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500'
                />
                <label className='text-sm font-medium text-gray-900 dark:text-white'>
                  管理者のみ
                </label>
              </div>
              <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>
                陸送員は自分のスケジュールのみ閲覧可能。
                他の陸送員のスケジュールは管理者・配車管理者のみ閲覧可能。
              </p>
            </div>
          </div>
        </div>

        {/* 全陸送員設定 */}
        <div
          className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
            visibility === DriverScheduleVisibility.ALL_DRIVERS
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
          }`}
          onClick={() => handleVisibilityChange(DriverScheduleVisibility.ALL_DRIVERS)}
        >
          <div className='flex items-start space-x-3'>
            <div
              className={`mt-1 ${
                visibility === DriverScheduleVisibility.ALL_DRIVERS
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              <UsersIcon className='h-5 w-5' />
            </div>
            <div className='flex-1'>
              <div className='flex items-center space-x-2'>
                <input
                  type='radio'
                  checked={visibility === DriverScheduleVisibility.ALL_DRIVERS}
                  onChange={() => handleVisibilityChange(DriverScheduleVisibility.ALL_DRIVERS)}
                  className='h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500'
                />
                <label className='text-sm font-medium text-gray-900 dark:text-white'>
                  全陸送員
                </label>
              </div>
              <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>
                同じ会社の陸送員同士でスケジュールを相互閲覧可能。
                チーム連携が必要な場合におすすめします。
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 現在の設定表示 */}
      <div className='bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4'>
        <div className='flex items-center space-x-2'>
          {visibility === DriverScheduleVisibility.ADMIN_ONLY ? (
            <EyeSlashIcon className='h-4 w-4 text-gray-500 dark:text-gray-400' />
          ) : (
            <EyeIcon className='h-4 w-4 text-gray-500 dark:text-gray-400' />
          )}
          <span className='text-sm text-gray-600 dark:text-gray-300'>
            現在の設定:
            <strong className='ml-1'>
              {visibility === DriverScheduleVisibility.ADMIN_ONLY
                ? '管理者のみ閲覧可能'
                : '全陸送員が相互閲覧可能'}
            </strong>
          </span>
        </div>
      </div>

      {/* 保存ボタン */}
      <div className='flex justify-end'>
        <Button variant='primary' onClick={handleSave} disabled={isLoading} className='px-6'>
          {isLoading ? '保存中...' : '設定を保存'}
        </Button>
      </div>
    </div>
  );
};

export default ScheduleVisibilitySettingsForm;
